/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';

import { OnepassItem } from 'client-code/onepass';

import { Settings } from '../../../core/device';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { TScreen } from '../../../core/navigation/types';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes, UniqueIdentifier } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import {
    onNetworkStateChangeOPMOperations,
    updateOnePassList,
} from '../../../store/settings/onepassmanager/onepassmanager.reducers';
import { showWhisper } from '../../../store/whisper/whisper.reducer';
import { Mousetrap, ScreenPreview } from '../../common';
import List from '../../common/List/List';
import { useScreenController } from '../../hooks';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { SettingsTemplate } from '../SettingsTemplate';
import { OnePassManagerInteractiveLayer } from './OnePassManagerInteractiveLayer';
import { OnePassManagerListItem } from './OnePassManagerListItem';

import './OnePassManagerScreen.scss';

const OnePassManagerScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'OnePassManagerScreen';

    const dispatch = useTivoDispatch();

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.ONEPASS_MANAGER_SETTINGS_SCREEN,
            screenController: ScreenControllerTypes.ONEPASS_MANAGER_SCREEN,
            taskController: ScreenTaskControllerTypes.ONEPASS_MANAGER_DEFAULT_TASKS,
        },
    });

    const services = new ServicesClientImpl();
    const shouldHideMetaData = services.shouldHideMetadata();

    const { onePassList, isScreenLoaded, isCaretVisible, lastSelectedPosition, offset } =
        useTivoSelector((state: RootState) => state.onePassManager);
    const { isOverlayDialogActive } = useTivoSelector((state: RootState) => state.overlayDialog);
    const [isCursorEnabled, setIsCursorEnabled] = useState(CursorManager.isCursorAppeared());
    const isVideoWindowEnabled = new Settings().isVideoWindowEnabled();

    const onNetworkStateChangeCallback = () => {
        const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

        if (isNetworkDisconnected) {
            dispatch(
                showWhisper({
                    message: NetworkManager.getNetworkDisconnectedMessage(),
                    iconType: WhisperTypes.INFO,
                }),
            );
        } else dispatch(onNetworkStateChangeOPMOperations());
    };

    const onCursorStateChangeCallback = (state) => {
        setIsCursorEnabled(state);
        if (!state) dispatch(setListEntryFocus(ListTypes.ONEPASS_MANAGER, true));
    };

    const renderEmptyOnePassList = () => {
        return (
            <div className={`empty-onepasses ${isVideoWindowEnabled ? 'with-video-window' : ''}`}>
                {Locale.getString(LocaleKeys.ONEPASS_MANAGER_EMPTY_LIST_MESSAGE)}
            </div>
        );
    };

    const renderOnePassList = () => {
        return (
            <>
                <div className="settings-common-content onepass-manager-content">
                    <List
                        type={ListTypes.ONEPASS_MANAGER}
                        itemList={onePassList}
                        initialFocusedIndex={lastSelectedPosition || 0}
                        initialSelectedIndex={lastSelectedPosition || 0}
                        classStyle={offset !== 0 ? 'chopped-list' : ''}
                        generateItemView={(item: OnepassItem, itemClass, index) => (
                            <Mousetrap
                                key={index}
                                value={`${index}_${ListTypes.ONEPASS_MANAGER}`}
                                className={`item ${itemClass}`}
                            >
                                <OnePassManagerListItem
                                    item={item}
                                    shouldHideMetaData={shouldHideMetaData}
                                />
                            </Mousetrap>
                        )}
                    />
                    {isCursorEnabled && <OnePassManagerInteractiveLayer focused={isCaretVisible} />}
                </div>
                <ScreenPreview classStyle="onepass-manager-preview" />
            </>
        );
    };

    const renderOnePasses = () => {
        return onePassList?.length ? renderOnePassList() : renderEmptyOnePassList();
    };

    useEffect(() => {
        if (isScreenLoaded && !isOverlayDialogActive) {
            dispatch(setListEntryFocus(ListTypes.ONEPASS_MANAGER, true));
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        dispatch(updateOnePassList(offset, lastSelectedPosition));
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            UniqueIdentifier.getNextOnePassManagerNetworkCallId();
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(refNetworkManager);
        };
    }, []);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.ONEPASS_MANAGER_TITLE)}>
            {isScreenLoaded && renderOnePasses()}
        </SettingsTemplate>
    );
};

export default OnePassManagerScreen;
